import { message } from 'antd';
import Webcam from 'react-webcam';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useReactMediaRecorder } from 'react-media-recorder-2';
import { getPreferredVideoCodec } from '@/utils/codec';
import { CaptureButton } from './CaptureButton';
import { Flex, VideoPreview } from '..';
import blobUrlToFile from '@/utils/blobUrlToFile';

export interface VideoCaptureProps {
  time?: number;
  loading?: boolean;
  onConfirm?: (file: File) => void;
  onError?: (error: string | DOMException) => void;
}

export function VideoCapture({
  time = 5000,
  loading,
  onError,
  onConfirm,
}: VideoCaptureProps) {

  const webcamRef = useRef<Webcam>(null);
  const [file, setFile] = useState(null);
  const [t] = useTranslation(['face']);

  const { status, clearBlobUrl, startRecording, stopRecording, mediaBlobUrl, previewStream } = useReactMediaRecorder({
    audio: false,
    video: true,
    stopStreamsOnStop: true,
    mediaRecorderOptions: {
      mimeType: getPreferredVideoCodec(),
    }
  });

  const handleStop = useCallback(() => {
    const tracks = previewStream?.getTracks() ?? [];
    for (const track of tracks) {
      if (track?.readyState === 'live') {
        track?.stop();
      }
    }
    stopRecording();
  }, [stopRecording, previewStream]);

  const onRecordClick = useCallback(async () => {
    if (!webcamRef.current) return;
    clearBlobUrl();
    startRecording();
    setTimeout(() => {
      handleStop();
    }, time);
    setFile(file);
  }, [time, webcamRef, clearBlobUrl, setFile, startRecording, handleStop]);

  useEffect(() => {
    return () => {
      handleStop();
    };
  }, []);

  return (
    <Flex direction="column" className="w-full h-full relative">
      {!mediaBlobUrl && status === 'idle' ?
        <Webcam
          ref={webcamRef}
          audio={false}
          playsInline
          forceScreenshotSourceSize={true}
          onUserMediaError={onError}
          className="h-full object-cover max-w-full max-h-full rounded-lg"
        /> :
        <VideoPreview mediaBlobUrl={mediaBlobUrl} stream={previewStream} />
      }
      <CaptureButton
        video
        time={time}
        src={mediaBlobUrl}
        loading={loading}
        onClick={onRecordClick}
        onRemove={() => clearBlobUrl()}
        captureText={t('face:labels.capture-video')}
        removeText={t('face:labels.remove-video')}
        onConfirm={async () => {
          if (onConfirm) {
            try {
              const file = await blobUrlToFile(mediaBlobUrl);
              onConfirm(file);
            }
            catch (e) {
              message.error(t('face:errors.convert-file'));
            }
          }
        }}
      />
    </Flex>
  );
}

export default VideoCapture;
